import {
  conversationListResponseSchema,
  conversationResponseSchema,
  type ConversationFilter,
  type ConversationItem,
  type ConversationListResponse,
} from '@chatapp-inbox/shared';
import { request } from './http';

export interface ListConversationsParams {
  filter: ConversationFilter;
  /** Busca por nome ou telefone (até 100 caracteres); ausente ou vazio = sem busca. */
  q?: string | undefined;
  /** `nextCursor` da página anterior; ausente = primeira página. */
  cursor?: string | null;
  limit?: number;
  signal?: AbortSignal;
}

export function listConversations(
  params: ListConversationsParams,
): Promise<ConversationListResponse> {
  return request('/conversations', {
    query: {
      filter: params.filter,
      q: params.q || undefined,
      cursor: params.cursor,
      limit: params.limit,
    },
    schema: conversationListResponseSchema,
    ...(params.signal ? { signal: params.signal } : {}),
  });
}

const conversationPath = (id: string): string => `/conversations/${encodeURIComponent(id)}`;

/** Uma conversa, como a lista a mostra (com `canWrite`). 404 = não existe OU a pessoa não a vê. */
export async function getConversation(id: string, signal?: AbortSignal): Promise<ConversationItem> {
  const { conversation } = await request(conversationPath(id), {
    schema: conversationResponseSchema,
    ...(signal ? { signal } : {}),
  });
  return conversation;
}

/**
 * Marca como lida. `upTo` = id da última mensagem que a pessoa VIU: o que chegou depois continua
 * não lido (sem ele, zera tudo). Só quem pode escrever na conversa (responsável ou admin) marca.
 */
export async function markRead(id: string, upTo?: string): Promise<ConversationItem> {
  const { conversation } = await request(`${conversationPath(id)}/read`, {
    method: 'POST',
    body: upTo ? { upTo } : {},
    schema: conversationResponseSchema,
  });
  return conversation;
}

/** Encerra a conversa (sai das abas de trabalho; o responsável continua o mesmo). Repetir é inofensivo. */
export async function closeConversation(id: string): Promise<ConversationItem> {
  const { conversation } = await request(`${conversationPath(id)}/close`, {
    method: 'POST',
    schema: conversationResponseSchema,
  });
  return conversation;
}

/** Reabre uma conversa encerrada. Mensagem do cliente ou nossa também a reabre sozinha. */
export async function reopenConversation(id: string): Promise<ConversationItem> {
  const { conversation } = await request(`${conversationPath(id)}/reopen`, {
    method: 'POST',
    schema: conversationResponseSchema,
  });
  return conversation;
}

/**
 * Atribui a conversa. Sem `userId`: assume para si; `null`: libera (volta a "Não atribuídas");
 * um id: transfere (só administrador).
 */
export async function assignConversation(
  id: string,
  userId?: string | null,
): Promise<ConversationItem> {
  const { conversation } = await request(`${conversationPath(id)}/assign`, {
    method: 'POST',
    body: userId === undefined ? {} : { userId },
    schema: conversationResponseSchema,
  });
  return conversation;
}
